import { useAtom } from 'jotai'
import { MouseEvent as ReactMouseEvent, useState } from 'react'
import { useEffectEvent } from '../../../utilities/use-effect-event'
import { BoundingBox } from './math'
import { transformerReducer } from './reducer'
import {
	transformerActionStateAtom,
	TransformerAnchor,
	transformerPreviewStateAtom,
	transformerStateAtom,
} from './state'
import { useTransformerStore } from './transformer-store'

export function TransformerGizmoScale({
	transformedRef,
	originalSize,
	startBoundingBox,
	anchor,
}: {
	transformedRef: React.RefObject<HTMLDivElement | null>
	originalSize: { width: number; height: number }
	startBoundingBox: BoundingBox
	anchor: TransformerAnchor // this is typically the OPPOSITE of the corner this is placed at.
}) {
	const store = useTransformerStore()
	const [actionState, setActionState] = useAtom(transformerActionStateAtom, { store })
	const [previewState, setPreviewState] = useAtom(transformerPreviewStateAtom, { store })
	const [mainState, setMainState] = useAtom(transformerStateAtom, { store })

	const [lastEventCoordinates, setLastEventCoordinates] = useState<{ x: number; y: number } | null>(null)

	const dragMoveHandler = useEffectEvent((event: MouseEvent) => {
		event.preventDefault()
		event.stopPropagation()
		setLastEventCoordinates({ x: event.pageX, y: event.pageY })
		setPreviewState(() => {
			return transformerReducer(mainState, {
				type: 'handleScaleChange',
				actionState: actionState,
				dragCoordinates: { x: event.pageX, y: event.pageY },
				forceCenterScale: event.altKey,
			})
		})
	})

	const keyDownHandler = useEffectEvent((event: KeyboardEvent) => {
		if (event.key === 'Alt' && lastEventCoordinates) {
			setPreviewState(() => {
				return transformerReducer(mainState, {
					type: 'handleScaleChange',
					actionState: actionState,
					dragCoordinates: lastEventCoordinates,
					forceCenterScale: true,
				})
			})
		}
	})

	const keyUpHandler = useEffectEvent((event: KeyboardEvent) => {
		if (event.key === 'Alt' && lastEventCoordinates) {
			setPreviewState(() => {
				return transformerReducer(mainState, {
					type: 'handleScaleChange',
					actionState: actionState,
					dragCoordinates: lastEventCoordinates,
					forceCenterScale: false,
				})
			})
		}
	})

	const dragEndHandler = useEffectEvent(() => {
		setActionState(null)
		if (previewState) {
			setMainState(previewState)
			setPreviewState(null)
		}

		window.removeEventListener('mousemove', dragMoveHandler)
		window.removeEventListener('mouseup', dragEndHandler)
		window.removeEventListener('keydown', keyDownHandler)
		window.removeEventListener('keyup', keyUpHandler)
	})

	const handleDragStart = useEffectEvent((event: ReactMouseEvent<HTMLDivElement>) => {
		event.preventDefault()
		event.stopPropagation()

		setActionState({
			type: 'scale',
			start: { x: event.pageX, y: event.pageY },
			center: {
				x: (transformedRef.current?.getBoundingClientRect().x ?? 0)
					+ (transformedRef.current?.getBoundingClientRect().width ?? 0) / 2,
				y: (transformedRef.current?.getBoundingClientRect().y ?? 0)
					+ (transformedRef.current?.getBoundingClientRect().height ?? 0) / 2,
			},
			anchor,
			startScale: mainState.scale,
			size: originalSize,
			startBoundingBox,
		})

		window.addEventListener('mousemove', dragMoveHandler)
		window.addEventListener('mouseup', dragEndHandler)
		window.addEventListener('keydown', keyDownHandler)
		window.addEventListener('keyup', keyUpHandler)
	})
	return (
		<div
			className="size-4 cursor-crosshair"
			onMouseDown={handleDragStart}
		>
			<div className="size-2 relative left-1 top-1 bg-[lime] ring ring-green-500/50" />
		</div>
	)
}
